"use client";

import { cn, onBtnClick } from "@humanex/ui/libs";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
}

export const Popup = ({
  isOpen,
  onClose,
  children,
  className,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true
}: PopupProps) => {
  const [root, setRoot] = useState<Element | null>(null);

  useEffect(() => {
    const el =
      document.body.querySelector("#root") || document.body.querySelector("#__next") || document.body;
    setRoot(el);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && closeOnEscape) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen || !root) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && closeOnBackdropClick) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-2 sm:p-4 bg-ink/40"
      onClick={handleBackdropClick}
    >
      <div
        className={cn(
          "relative bg-white rounded-xl shadow-lg max-w-md w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto",
          "border border-gray-200",
          "transform transition-all duration-300 ease-out",
          "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4",
          className
        )}
      >
        {showCloseButton && (
          <button
            onClick={onBtnClick(onClose)}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-surface transition-colors z-10"
            aria-label="Close"
          >
            x
          </button>
        )}
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </div>,
    root
  );
};
